import { type Virtualizer } from '@tanstack/react-virtual';
import { type MutableRefObject } from 'react';

export const WORKSPACE_AGENT_DISPLAY_ROW_ID_ATTRIBUTE = 'data-workspace-agent-display-row-id';

type ScrollWorkspaceAgentDisplayRowStartIntoViewParams = {
  displayRowId: string;
  displayRowIndex: number;
  isPinnedToBottomRef: MutableRefObject<boolean | undefined>;
  scrollContainerRef: MutableRefObject<HTMLElement | null>;
  suppressMeasureTailPinRef: MutableRefObject<boolean | undefined>;
  virtualizer: Virtualizer<HTMLElement, Element>;
};

const scrollRowStartIntoView = ({
  displayRowId,
  displayRowIndex,
  scrollContainerRef,
  virtualizer,
}: Pick<
  ScrollWorkspaceAgentDisplayRowStartIntoViewParams,
  'displayRowId' | 'displayRowIndex' | 'scrollContainerRef' | 'virtualizer'
>) => {
  const container = scrollContainerRef.current;

  if (!container) {
    return;
  }

  const rowElement = container.querySelector<HTMLElement>(
    `[${WORKSPACE_AGENT_DISPLAY_ROW_ID_ATTRIBUTE}="${displayRowId}"]`,
  );

  if (rowElement) {
    const containerTop = container.getBoundingClientRect().top;
    const rowTop = rowElement.getBoundingClientRect().top;
    const delta = rowTop - containerTop;

    if (delta < 0) {
      container.scrollTop += delta;
    }

    return;
  }

  virtualizer.scrollToIndex(displayRowIndex, { align: 'start' });
};

export const scrollWorkspaceAgentDisplayRowStartIntoView = ({
  displayRowId,
  displayRowIndex,
  isPinnedToBottomRef,
  scrollContainerRef,
  suppressMeasureTailPinRef,
  virtualizer,
}: ScrollWorkspaceAgentDisplayRowStartIntoViewParams) => {
  if (displayRowIndex < 0) {
    return;
  }

  isPinnedToBottomRef.current = false;
  suppressMeasureTailPinRef.current = true;

  const run = () => {
    scrollRowStartIntoView({ displayRowId, displayRowIndex, scrollContainerRef, virtualizer });
  };

  requestAnimationFrame(() => {
    run();
    requestAnimationFrame(() => {
      run();
      suppressMeasureTailPinRef.current = false;
    });
  });
};
